import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Life Member", "Patron", "Volunteer", "Donor"];
const STATUSES = ["Active", "Pending", "Inactive"];
const empty = { full_name: "", mobile: "", email: "", membership_type: "Life Member", status: "Active" };

const statusColor = {
  Active: "bg-[#E8F0EA] text-[#194324]",
  Pending: "bg-[#F9F3E3] text-[#A8811A]",
  Inactive: "bg-[#F2EEE5] text-[#5C7264]",
};

export default function MembersAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const [appPw, setAppPw] = useState("");

  const saveAppPw = async (e) => {
    e.preventDefault();
    if (appPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try { await api.put(`/admin/members/${pwFor.id}/app-password`, { password: appPw }); toast.success("App password set. Share it privately with the member."); setPwFor(null); setAppPw(""); load(); }
    catch { toast.error("Could not set app password"); }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/members").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ full_name: m.full_name, mobile: m.mobile, email: m.email, membership_type: m.membership_type, status: m.status }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/members/${editing.id}`, form);
      else await api.post("/admin/members", form);
      toast.success(editing ? "Member updated" : "Member added");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error("Failed to save member");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/members/${deleteId}`);
      toast.success("Member deleted");
      load();
    } catch { toast.error("Delete failed"); }
    setDeleteId(null);
  };

  return (
    <div data-testid="admin-members-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">Members</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Trust Members</h1>
        </div>
        <Button onClick={openAdd} className="bg-[#194324] hover:bg-[#2D623A] text-white rounded-full" data-testid="admin-add-member-button">
          <Plus className="h-4 w-4 mr-2" /> Add Member
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#5C7264]"><Users className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />No members yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EEE5]">
                  <TableHead>Member ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                    <TableCell className="font-mono text-xs text-[#B38728]">{m.member_id}</TableCell>
                    <TableCell className="font-medium text-[#194324]">{m.full_name}</TableCell>
                    <TableCell>{m.mobile}</TableCell>
                    <TableCell className="text-sm">{m.email}</TableCell>
                    <TableCell>{m.membership_type}</TableCell>
                    <TableCell><Badge className={`${statusColor[m.status] || ""} border-0`}>{m.status}</Badge>{m.has_app_login && <Badge className="ml-1 border-0 bg-[#F9F3E3] text-[#A8811A]" data-testid={`member-app-login-${m.id}`}>App</Badge>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" title="Set app password" onClick={() => { setPwFor(m); setAppPw(""); }} data-testid={`member-app-password-${m.id}`}><KeyRound className="h-4 w-4 text-[#B38728]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`member-edit-${m.id}`}><Pencil className="h-4 w-4 text-[#2D623A]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(m.id)} data-testid={`member-delete-${m.id}`}><Trash2 className="h-4 w-4 text-[#D9531E]" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{editing ? "Edit Member" : "Add Member"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="member-form-full-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="member-form-mobile" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="member-form-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Membership Type</Label>
                <Select value={form.membership_type} onValueChange={(v) => setForm({ ...form, membership_type: v })}>
                  <SelectTrigger data-testid="member-form-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="member-form-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid="member-form-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Add Member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">Set App Password</DialogTitle></DialogHeader>
          <form onSubmit={saveAppPw} className="space-y-4">
            <p className="text-sm text-[#5C7264]">For {pwFor?.full_name} ({pwFor?.member_id}). Use this for website-joined members or a forgotten password. The member logs in with their email once their status is Active; any existing app sessions are signed out.</p>
            <div className="space-y-2"><Label>New app password (min. 8 characters)</Label><Input type="password" value={appPw} onChange={(e) => setAppPw(e.target.value)} autoComplete="new-password" data-testid="member-app-password-input" /></div>
            <DialogFooter><Button type="submit" className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid="member-app-password-submit">Set Password</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete member?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D9531E] hover:bg-[#b8441a]" data-testid="confirm-delete-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
